import React, { useState, useContext } from 'react';
import { Search, ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { semanticBrandingService } from '../../services/semanticBranding';
import { authService } from '../../services/auth';
import { ToastContext } from '../../App';
import { LoginModalContext } from '../../App';
// Lista de emails permitidos para ver o toggle de recorrência
const ALLOWED_EMAILS = ['nicolas.simionato@conversion.com.br', 'gabriel.vieira@conversion.com.br', 'leonan.sacchi@conversion.com.br'];
const HeroSection = ({
  isLoggedIn,
  onStartAnalysis
}) => {
  const [brand, setBrand] = useState('');
  const [url, setUrl] = useState('');
  const [recorrencia, setRecorrencia] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showGeoTooltip, setShowGeoTooltip] = useState(false);
  const [showBrandingTooltip, setShowBrandingTooltip] = useState(false);
  const navigate = useNavigate();
  const {
    addToast
  } = useContext(ToastContext);
  const {
    openLoginModal
  } = useContext(LoginModalContext);
  // Check if user is in the allowed list
  const user = authService.getCurrentUser();
  const isAllowedUser = user?.email && ALLOWED_EMAILS.includes(user.email);
  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    // Pass the brand data to the onStartAnalysis function
    const brandData = {
      brand,
      url,
      recorrencia
    };
    if (isLoggedIn) {
      try {
        // Create a new brand document
        const response = await semanticBrandingService.createBrandDocument(brand, url, true, recorrencia);
        // Navigate to the report page with the document ID and isNewDocument flag
        navigate(`/relatorio/${response.brandDocument.id}`, {
          state: {
            isNewDocument: true
          }
        });
        addToast('success', 'Análise iniciada com sucesso!');
      } catch (error) {
        console.error('Failed to create brand document:', error);
        addToast('error', error.response?.data?.message || 'Erro ao criar análise. Tente novamente.');
      } finally {
        setIsLoading(false);
      }
    } else {
      // If not logged in, trigger the login/register flow with the brand data
      setTimeout(() => {
        onStartAnalysis(brandData);
        setIsLoading(false);
      }, 500);
    }
  };
  return <section className="relative w-full overflow-hidden bg-white min-h-[90vh] flex items-center pt-8" data-id="element-573" data-mp="1x48tp3feeyg9">
      {/* Enhanced AI-themed background pattern with smooth gradient transition */}
      <div className="absolute inset-0 z-0" data-id="element-574" data-mp="265fa7ci1s99i">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-white" data-id="element-575" data-mp="2ay074mw932h9"></div>
        <div className="absolute top-0 left-0 w-full h-full opacity-10" data-id="element-576" data-mp="1dicfywkemkp2">
          <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-[#1649ff] blur-lg" data-id="element-577" data-mp="1dg8gcqtjg7ul"></div>
          <div className="absolute top-40 right-20 w-16 h-16 rounded-full bg-[#1649ff] blur-lg" data-id="element-578" data-mp="1iet4hrvin12o"></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 rounded-full bg-[#1649ff] blur-lg" data-id="element-579" data-mp="1avaf93voibln"></div>
          <div className="absolute top-1/3 right-1/3 w-12 h-12 rounded-full bg-[#1649ff] blur-lg" data-id="element-580" data-mp="10zguix7t3bss"></div>
        </div>
        {/* Smooth gradient transition at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-gradient-to-t from-white to-transparent" data-id="element-581" data-mp="wxlxi4wwrkkt"></div>
      </div>
      <div className="container relative z-10 px-4 mx-auto sm:px-6 lg:px-8 flex flex-col justify-center items-center py-10" data-id="element-582" data-mp="n82dgvxglkod">
        <div className="max-w-4xl mx-auto text-center mb-10" data-id="element-583" data-mp="i87lep1y6p1q">
          {/* Badge above h1 with darker gray background, increased shadow, and enhanced floating animation */}
          <div className="inline-flex items-center mb-5 px-4 py-1.5 bg-gray-600 text-white text-sm font-medium rounded-full border border-gray-600 shadow-lg" data-id="element-584" data-mp="98azgv6y04ad">
            Agente de IA
            <span role="img" aria-label="robot" className="ml-2" data-id="element-585" data-mp="4bkcrp0muafz">
              🤖
            </span>
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bLJxja7VGTicz6MvVUWEHu/sparkles_2728.png" alt="Sparkle" className="w-5 h-5 ml-2" style={{
            animation: '3s ease-in-out infinite',
            animationName: 'sparkleFloat',
            '@keyframes sparkleFloat': {
              '0%': {
                transform: 'translate(0px, 0px) rotate(0deg)'
              },
              '25%': {
                transform: 'translate(10px, -10px) rotate(10deg)'
              },
              '50%': {
                transform: 'translate(0px, 10px) rotate(0deg)'
              },
              '75%': {
                transform: 'translate(-10px, -10px) rotate(-10deg)'
              },
              '100%': {
                transform: 'translate(0px, 0px) rotate(0deg)'
              }
            }
          }} data-id="element-586" data-mp="24dtnxfzb0j0i" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-5xl leading-tight mb-6 max-w-4xl mx-auto" data-id="element-587" data-mp="3g4zz19z8usha">
            <span className="block" data-id="element-588" data-mp="3tx6c2n73ojci">
              Faça uma análise de branding semântico,
            </span>
            <span className="block text-[#1649ff]" data-id="element-589" data-mp="3p3v5mn67a4l4">
              melhore sua presença nas IAs
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-590" data-mp="7r11i3wmiz1o">
            Faça uma análise de{' '}
            <span className="relative cursor-pointer border-b-2 border-dotted border-gray-500 inline-flex items-center" onMouseEnter={() => setShowBrandingTooltip(true)} onMouseLeave={() => setShowBrandingTooltip(false)} onClick={() => window.open('https://www.conversion.com.br/blog/branding-semantico/', '_blank', 'noopener,noreferrer')} data-id="element-591" data-mp="gpcsw85be0v6">
              Branding Semântico
              {showBrandingTooltip && <span className="absolute z-10 left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-72 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg" data-id="element-592" data-mp="rcxqhujnslg3">
                  Branding Semântico é um conjunto de ações e estratégias para
                  relacionar marcas à atributos, dores do cliente e questões
                  para aparecer nos resultados de IA.
                  <span className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-8 border-solid border-transparent border-t-gray-800" data-id="element-593" data-mp="15aw0knqy5vps"></span>
                </span>}
            </span>{' '}
            para entender como as Inteligências Artificiais reconhecem sua marca
            e potencialize seu{' '}
            <span className="relative cursor-pointer border-b-2 border-dotted border-gray-500 inline-flex items-center" onMouseEnter={() => setShowGeoTooltip(true)} onMouseLeave={() => setShowGeoTooltip(false)} onClick={() => window.open('https://www.conversion.com.br/blog/generative-engine-opitmization/', '_blank', 'noopener,noreferrer')} data-id="element-594" data-mp="1o2ox2s585z29">
              GEO
              {showGeoTooltip && <span className="absolute z-10 left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-72 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg" data-id="element-595" data-mp="3sb1g8dem4ld1">
                  GEO (Generative Engine Optimization) é a otimização para
                  marcas e conteúdos aparecerem nas respostas de IAs generativas
                  – ChatGPT, Claude, Gemini, Perplexity, etc.
                  <span className="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-8 border-solid border-transparent border-t-gray-800" data-id="element-596" data-mp="evmzlktdkvho"></span>
                </span>}
            </span>
            .
          </p>
        </div>
        <div className="max-w-2xl mx-auto w-full" data-id="element-597" data-mp="3jcepewfz5aw0">
          <div className="p-4 md:p-6 bg-white rounded-xl shadow-lg border border-gray-100" data-id="element-598" data-mp="3je7wudyevz52">
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-599" data-mp="35inhttlt012x">
              <div data-id="element-600" data-mp="39n4znn8ls4b2">
                <label htmlFor="brand" className="block mb-1 text-sm font-medium text-gray-700" data-id="element-601" data-mp="39mlhc5f1jdwi">
                  Nome da marca
                </label>
                <div className="relative" data-id="element-602" data-mp="64troxodn61h">
                  <input type="text" id="brand" value={brand} onChange={e => setBrand(e.target.value)} placeholder="Ex: Conversion" className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} data-id="element-603" data-mp="3obrezoy8cw2a" />
                  <Search className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-604" data-mp="25ck021tr8tgr" />
                </div>
              </div>
              <div data-id="element-605" data-mp="2edhxu2dlo4qw">
                <label htmlFor="url" className="block mb-1 text-sm font-medium text-gray-700" data-id="element-606" data-mp="2ee9s99vgnwfl">
                  URL do site
                </label>
                <input type="url" id="url" name="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="Ex: https://www.conversion.com.br" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1649ff] text-gray-900" required disabled={isLoading} autoComplete="off" data-id="element-607" data-mp="1lplzkt8du4s0" />
              </div>
              {/* Recorrência toggle - only visible for specific allowed users */}
              {isLoggedIn && isAllowedUser && <div className="flex items-center gap-3" data-id="element-608" data-mp="30icuhk5d5oma">
                  <label htmlFor="recorrencia" className="text-sm font-medium text-gray-700" data-id="element-609" data-mp="30kz19hkfk03g">
                    Recorrência
                  </label>
                  <button type="button" role="switch" aria-checked={recorrencia} onClick={() => setRecorrencia(!recorrencia)} disabled={isLoading} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#1649ff] focus:ring-offset-2 ${recorrencia ? 'bg-[#1649ff]' : 'bg-gray-300'} ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-610" data-mp="2mrhlm7uv77ip">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${recorrencia ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-611" data-mp="ja1gpbwtinvd" />
                  </button>
                </div>}
              <button type="submit" className="w-full py-3 text-white bg-[#1649ff] rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center transition-colors" disabled={isLoading} data-id="element-612" data-mp="xam2mgy14zt3">
                {isLoading ? <>
                    <Loader2 size={18} className="mr-2 animate-spin" data-id="element-613" data-mp="eb855p13d6xu" />
                    Processando...
                  </> : <>
                    Gerar relatório <ArrowRight size={18} className="ml-2" data-id="element-614" data-mp="3i0l54awb2gob" />
                  </>}
              </button>
            </form>
          </div>
          <div className="mt-3 text-center text-sm text-gray-500" data-id="element-615" data-mp="39ykhk2mgej1u">
            Análise completa em menos de 5 minutos. 100% gratuito.
          </div>
        </div>
      </div>
    </section>;
};
export default HeroSection;